import React, { useState } from 'react';
import {
  Eye,
  CheckCircle,
  Zap,
  Network,
  Layers,
  MessageSquare,
  Target,
  Users,
  Gauge,
  BookOpen,
  Map,
  Settings,
  Lightbulb as LightbulbIcon,
  Heart as HeartIcon,
  Zap as ZapIcon,
  Eye as EyeIcon,
  Filter as FilterIcon,
  Brain as BrainIcon
} from 'lucide-react';

export const LadderOfInferenceM2Model: React.FC = () => {
  const [selectedFormat, setSelectedFormat] = useState<'slide' | 'handout' | 'field-card' | 'poster' | 'mobile'>('slide');
  const [selectedContext, setSelectedContext] = useState<'coaching' | 'educational' | 'organizational' | 'personal'>('coaching');

  // M3 Metadata for Ladder of Inference
  const m3Metadata = {
    version: 3,
    isEmbedded: true,
    ontology: {
      categories: ['Framework', 'Method', 'Tool'],
      structures: ['Sequence', 'Hierarchy'],
      relationships: {
        'is-a': ['Framework', 'Cognitive Tool', 'Decision-Making Aid'],
        'part-of': ['Coaching Toolkit', 'Personal Growth Framework', 'Peak Priorities Base Camp'],
        'applies-to': ['Conflict Resolution', 'Communication', 'Self-Reflection', 'Team Dynamics'],
        'used-by': ['Coaches', 'Leaders', 'Individuals', 'Teams']
      },
      systems: ['Peak Priorities Base Camp', 'Coaching Toolkit', 'Personal Growth Framework']
    },
    modality: {
      attributes: {
        'Impact/Value': 8,
        'Effort/Complexity': 6,
        'Clarity/Ambiguity': 7,
        'Learning Value': 9,
        'Energy/Motivation': 7
      },
      states: {
        'Development Status': 'Ready',
        'Readiness': 'Ready',
        'Health': 'Green',
        'Confidence': 8
      },
      contexts: {
        'Scope': 'Individual',
        'Environment': 'Calm',
        'Domain': 'Coaching',
        'Temporal': 'Ongoing'
      }
    },
    dynamics: {
      rules: ['Reflection Requirement', 'Evidence Weighting', 'Thresholds'],
      processes: ['Ascent Process', 'Descent Process', 'Weekly Reflection'],
      causality: ['Direct Cause → Effect', 'Feedback Loops', 'Delayed Effects']
    },
    evaluation: {
      intent: {
        'Actor Scope': 'Individual',
        'Stakeholders': 'Coachee',
        'Time Horizon': 'Mid-term',
        'Motivational Frame': 'Intrinsic'
      },
      utility: {
        'Outcome Speed': 7,
        'Resilience': 8,
        'Clarity/Coherence': 9,
        'Learning Value': 9,
        'Customer Value': 8
      }
    }
  };

  // Visual Signature Elements
  const visualSignatures = {
    colorCoding: {
      primary: 'primary', // Framework
      secondary: 'sage',  // Health/Ready
      accent: 'amber'     // High Learning Value
    },
    iconBadges: [
      { icon: <Layers className="h-4 w-4" />, label: 'Framework', color: 'primary' },
      { icon: <MessageSquare className="h-4 w-4" />, label: 'Coaching', color: 'sage' },
      { icon: <Target className="h-4 w-4" />, label: 'Individual', color: 'amber' }
    ],
    statusIndicators: [
      { type: 'dot', color: 'sage', label: 'Ready' },
      { type: 'flag', color: 'sage', label: 'Healthy' }
    ],
    metadataBars: [
      { label: 'Impact', value: 8, max: 10, color: 'primary' },
      { label: 'Learning', value: 9, max: 10, color: 'amber' },
      { label: 'Clarity', value: 7, max: 10, color: 'sage' }
    ]
  };

  const getColorClasses = (color: string) => {
    switch (color) {
      case 'primary':
        return 'bg-consulting-slate-blue-100 border-consulting-slate-blue-300 text-consulting-slate-blue-800';
      case 'sage':
        return 'bg-sage-100 border-sage-300 text-sage-800';
      case 'amber':
        return 'bg-[var(--mustard-dim)] border-[var(--mustard)] text-[var(--mustard)]';
      case 'luminous-coral':
        return 'bg-luminous-coral-100 border-luminous-coral-300 text-luminous-coral-800';
      default:
        return 'bg-[var(--hover-tint)] border-[var(--rule)] text-[var(--text-pri)]';
    }
  };

  const renderLadderRungs = () => {
    const rungs = [
      { id: 1, title: 'Observable Data', icon: <EyeIcon className="h-4 w-4" />, color: visualSignatures.colorCoding.primary, description: 'Facts and events we can observe' },
      { id: 2, title: 'Selected Data', icon: <FilterIcon className="h-4 w-4" />, color: visualSignatures.colorCoding.primary, description: 'What we choose to notice' },
      { id: 3, title: 'Interpreted Meaning', icon: <BrainIcon className="h-4 w-4" />, color: visualSignatures.colorCoding.secondary, description: 'What we think it means' },
      { id: 4, title: 'Assumptions Made', icon: <LightbulbIcon className="h-4 w-4" />, color: visualSignatures.colorCoding.secondary, description: 'Beliefs we bring to the situation' },
      { id: 5, title: 'Conclusions Drawn', icon: <CheckCircle className="h-4 w-4" />, color: visualSignatures.colorCoding.accent, description: 'Judgments we make' },
      { id: 6, title: 'Beliefs Formed', icon: <HeartIcon className="h-4 w-4" />, color: visualSignatures.colorCoding.accent, description: 'What we come to believe' },
      { id: 7, title: 'Actions Taken', icon: <ZapIcon className="h-4 w-4" />, color: visualSignatures.colorCoding.accent, description: 'What we do as a result' }
    ];

    return rungs.map((rung) => (
      <div key={rung.id} className="relative flex items-center mb-3">
        <div className="absolute left-6 w-4 h-4 bg-[var(--bg-raised)] border-2 border-charcoal-600 rounded-full flex items-center justify-center text-xs font-bold text-charcoal-600 z-10">
          {rung.id}
        </div>
        
        <div className={`ml-16 h-16 w-full  border-2 ${getColorClasses(rung.color)} p-3 flex items-center gap-3`}>
          <div className="flex-shrink-0">
            {rung.icon}
          </div>
          <div className="flex-1">
            <div className="text-sm font-medium">{rung.title}</div>
            <div className="text-xs opacity-75">{rung.description}</div>
          </div>
        </div>
      </div>
    ));
  };

  const renderVisualSignatures = () => (
    <div className="space-y-4">
      {/* Icon Badges */}
      <div className="flex items-center gap-2">
        {visualSignatures.iconBadges.map((badge, index) => (
          <div key={index} className={`px-2 py-1 rounded-full border text-xs flex items-center gap-1 ${getColorClasses(badge.color)}`}>
            {badge.icon}
            <span>{badge.label}</span>
          </div>
        ))}
        {visualSignatures.statusIndicators.map((indicator, index) => (
          <div key={index} className={`w-3 h-3 rounded-full ${indicator.color === 'sage' ? 'bg-sage-500' : 'bg-[var(--mustard)]'}`} title={indicator.label}></div>
        ))}
      </div>

      {/* Metadata Bars */}
      <div className="space-y-2">
        {visualSignatures.metadataBars.map((bar, index) => (
          <div key={index} className="flex items-center gap-2">
            <span className="text-xs font-medium w-16">{bar.label}</span>
            <div className="flex-1 bg-[var(--hover-tint)] rounded-full h-2">
              <div 
                className={`h-2 rounded-full ${bar.color === 'primary' ? 'bg-consulting-slate-blue-600' : bar.color === 'amber' ? 'bg-[var(--mustard)]' : 'bg-sage-600'}`}
                style={{ width: `${(bar.value / bar.max) * 100}%` }}
              ></div>
            </div>
            <span className="text-xs w-6">{bar.value}</span>
          </div>
        ))}
      </div>
    </div>
  );

  const renderM3Metadata = () => (
    <div className="grid md:grid-cols-2 gap-6">
      {/* Ontology */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-charcoal-900 flex items-center gap-2">
          <Network className="h-5 w-5" />
          Ontology
        </h3>
        <div className="space-y-3">
          <div>
            <div className="text-sm font-medium text-charcoal-700">Categories</div>
            <div className="flex flex-wrap gap-1 mt-1">
              {m3Metadata.ontology.categories.map((category, index) => (
                <span key={index} className="px-2 py-1 bg-consulting-slate-blue-100 text-consulting-slate-blue-800 rounded text-xs">
                  {category}
                </span>
              ))}
            </div>
          </div>
          <div>
            <div className="text-sm font-medium text-charcoal-700">Systems</div>
            <div className="space-y-1 mt-1">
              {m3Metadata.ontology.systems.map((system, index) => (
                <div key={index} className="text-xs text-charcoal-600">• {system}</div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Modality */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-charcoal-900 flex items-center gap-2">
          <Gauge className="h-5 w-5" />
          Modality
        </h3>
        <div className="space-y-3">
          <div>
            <div className="text-sm font-medium text-charcoal-700">Key Attributes</div>
            <div className="space-y-1 mt-1">
              {Object.entries(m3Metadata.modality.attributes).slice(0, 3).map(([key, value]) => (
                <div key={key} className="flex items-center gap-2">
                  <span className="text-xs w-20">{key}</span>
                  <div className="flex-1 bg-[var(--hover-tint)] rounded-full h-1">
                    <div 
                      className="bg-consulting-slate-blue-600 h-1 rounded-full"
                      style={{ width: `${(value / 10) * 100}%` }}
                    ></div>
                  </div>
                  <span className="text-xs w-4">{value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderFormatSpecific = () => {
    switch (selectedFormat) {
      case 'slide':
        return (
          <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8">
            <h2 className="text-2xl font-bold text-charcoal-900 mb-6 text-center">The Ladder of Inference</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-lg font-semibold text-charcoal-900 mb-4">Visual Framework</h3>
                <div className="relative">
                  <div className="absolute left-8 top-0 bottom-0 w-1 bg-charcoal-300"></div>
                  {renderLadderRungs()}
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-charcoal-900 mb-4">Key Insights</h3>
                <div className="space-y-3">
                  <div className="p-3 bg-sage-50 border border-sage-200">
                    <div className="text-sm font-medium text-sage-800">Reflection Requirement</div>
                    <div className="text-xs text-sage-700">Always question assumptions before acting</div>
                  </div>
                  <div className="p-3 bg-[var(--mustard-dim)] border border-[var(--mustard)]">
                    <div className="text-sm font-medium text-[var(--mustard)]">High Learning Value</div>
                    <div className="text-xs text-[var(--mustard)]">Teaches systematic thinking patterns</div>
                  </div>
                </div>
                {renderVisualSignatures()}
              </div>
            </div>
          </div>
        );

      case 'handout':
        return (
          <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8">
            <h2 className="text-2xl font-bold text-charcoal-900 mb-6 text-center">Ladder of Inference: A Framework for Better Thinking</h2>
            <div className="space-y-6">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-lg font-semibold text-charcoal-900 mb-4">The Framework</h3>
                  <div className="relative">
                    <div className="absolute left-8 top-0 bottom-0 w-1 bg-charcoal-300"></div>
                    {renderLadderRungs()}
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-charcoal-900 mb-4">M3 Metadata</h3>
                  {renderM3Metadata()}
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-charcoal-900 mb-4">Visual Signatures</h3>
                {renderVisualSignatures()}
              </div>
            </div>
          </div>
        );

      case 'field-card':
        return (
          <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-6">
            <div className="space-y-4">
              {/* Compact Header */}
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold text-charcoal-900">Ladder of Inference</h2>
                <div className="flex items-center gap-1">
                  {visualSignatures.iconBadges.slice(0, 2).map((badge, index) => (
                    <div key={index} className={`w-6 h-6 rounded-full border flex items-center justify-center ${getColorClasses(badge.color)}`}>
                      {badge.icon}
                    </div>
                  ))}
                </div>
              </div>

              {/* Compact Ladder */}
              <div className="space-y-1">
                {[
                  { id: 1, title: 'Data', color: visualSignatures.colorCoding.primary },
                  { id: 2, title: 'Select', color: visualSignatures.colorCoding.primary },
                  { id: 3, title: 'Interpret', color: visualSignatures.colorCoding.secondary },
                  { id: 4, title: 'Assume', color: visualSignatures.colorCoding.secondary },
                  { id: 5, title: 'Conclude', color: visualSignatures.colorCoding.accent },
                  { id: 6, title: 'Believe', color: visualSignatures.colorCoding.accent },
                  { id: 7, title: 'Act', color: visualSignatures.colorCoding.accent }
                ].map((rung) => (
                  <div key={rung.id} className={`h-6 rounded border-l-4 ${getColorClasses(rung.color)} flex items-center px-2`}>
                    <span className="text-xs font-medium">{rung.id}. {rung.title}</span>
                  </div>
                ))}
              </div>

              {/* Compact Metadata */}
              <div className="flex items-center gap-2 text-xs">
                <span>Impact: {m3Metadata.modality.attributes['Impact/Value']}</span>
                <span>•</span>
                <span>Learning: {m3Metadata.modality.attributes['Learning Value']}</span>
                <span>•</span>
                <span>Ready</span>
              </div>
            </div>
          </div>
        );

      case 'poster':
        return (
          <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8">
            <h2 className="text-3xl font-bold text-charcoal-900 mb-8 text-center">The Ladder of Inference</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="md:col-span-2">
                <div className="relative">
                  <div className="absolute left-8 top-0 bottom-0 w-1 bg-charcoal-300"></div>
                  {renderLadderRungs()}
                </div>
              </div>
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-charcoal-900 mb-4">Framework Details</h3>
                  {renderM3Metadata()}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-charcoal-900 mb-4">Visual Signatures</h3>
                  {renderVisualSignatures()}
                </div>
              </div>
            </div>
          </div>
        );

      case 'mobile':
        return (
          <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-4">
            <div className="space-y-3">
              {/* Mobile Header */}
              <div className="flex items-center justify-between">
                <h2 className="text-base font-bold text-charcoal-900">Ladder of Inference</h2>
                <div className="flex items-center gap-1">
                  <div className={`w-4 h-4 rounded-full border ${getColorClasses(visualSignatures.colorCoding.primary)}`}></div>
                  <div className={`w-2 h-2 rounded-full ${visualSignatures.statusIndicators[0].color === 'sage' ? 'bg-sage-500' : 'bg-[var(--mustard)]'}`}></div>
                </div>
              </div>

              {/* Mobile Ladder */}
              <div className="space-y-1">
                {[1, 2, 3, 4, 5, 6, 7].map((id) => (
                  <div key={id} className={`h-4 rounded border-l-2 ${getColorClasses(visualSignatures.colorCoding.primary)}`}></div>
                ))}
              </div>

              {/* Mobile Metadata */}
              <div className="text-xs text-center">
                {m3Metadata.modality.attributes['Impact/Value']}/{m3Metadata.modality.attributes['Learning Value']}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center space-y-4">
        <div className="flex items-center justify-center gap-3 mb-2">
          <Layers className="h-10 w-10 text-consulting-slate-blue-600" />
          <h1 className="text-4xl font-bold text-charcoal-900">Ladder of Inference M2 Model</h1>
        </div>
        <p className="text-lg text-charcoal-600 max-w-4xl mx-auto">
          A concrete M2 model with M3 metadata applied, demonstrating how visual signature elements 
          encode semantic meaning across different artifact formats and contexts.
        </p>
      </div>

      {/* Format and Context Selection */}
      <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8">
        <h2 className="text-2xl font-bold text-charcoal-900 mb-6 text-center">Format & Context Selection</h2>
        <div className="grid md:grid-cols-2 gap-8">
          <div>
            <h3 className="text-lg font-semibold text-charcoal-900 mb-4">Format</h3>
            <div className="grid grid-cols-2 gap-2">
              {[
                { id: 'slide', name: 'PowerPoint Slide', icon: <Eye className="h-4 w-4" /> },
                { id: 'handout', name: 'Handout', icon: <BookOpen className="h-4 w-4" /> },
                { id: 'field-card', name: 'Field Card', icon: <Target className="h-4 w-4" /> },
                { id: 'poster', name: 'Poster', icon: <Map className="h-4 w-4" /> },
                { id: 'mobile', name: 'Mobile', icon: <Settings className="h-4 w-4" /> }
              ].map((format) => (
                <button
                  key={format.id}
                  onClick={() => setSelectedFormat(format.id as any)}
                  className={`p-3  border-2 transition-all text-left ${
                    selectedFormat === format.id
                      ? 'border-consulting-slate-blue-600 bg-consulting-slate-blue-50 text-consulting-slate-blue-800'
                      : 'border-[var(--rule)] bg-[var(--bg-raised)] text-charcoal-700 hover:border-[var(--rule)]'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {format.icon}
                    <span className="text-sm font-medium">{format.name}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-charcoal-900 mb-4">Context</h3>
            <div className="space-y-2">
              {[
                { id: 'coaching', name: 'Coaching Professional', icon: <MessageSquare className="h-4 w-4" /> },
                { id: 'educational', name: 'Educational & Teaching', icon: <BookOpen className="h-4 w-4" /> },
                { id: 'organizational', name: 'Organizational Team', icon: <Users className="h-4 w-4" /> },
                { id: 'personal', name: 'Personal Development', icon: <Target className="h-4 w-4" /> }
              ].map((context) => (
                <button
                  key={context.id}
                  onClick={() => setSelectedContext(context.id as any)}
                  className={`p-3  border-2 transition-all text-left w-full ${
                    selectedContext === context.id
                      ? 'border-sage-600 bg-sage-50 text-sage-800'
                      : 'border-[var(--rule)] bg-[var(--bg-raised)] text-charcoal-700 hover:border-[var(--rule)]'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {context.icon}
                    <span className="text-sm font-medium">{context.name}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Format-Specific Rendering */}
      {renderFormatSpecific()}

      {/* M3 Metadata Summary */}
      <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8">
        <h2 className="text-2xl font-bold text-charcoal-900 mb-6 text-center">M3 Metadata Summary</h2>
        <div className="grid md:grid-cols-4 gap-6">
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-charcoal-900 flex items-center gap-2">
              <Network className="h-5 w-5" />
              Ontology
            </h3>
            <div className="space-y-2">
              <div className="text-sm">
                <div className="font-medium">Categories:</div>
                <div className="text-charcoal-600">{m3Metadata.ontology.categories.join(', ')}</div>
              </div>
              <div className="text-sm">
                <div className="font-medium">Systems:</div>
                <div className="text-charcoal-600">{m3Metadata.ontology.systems.length} systems</div>
              </div>
            </div>
          </div>
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-charcoal-900 flex items-center gap-2">
              <Gauge className="h-5 w-5" />
              Modality
            </h3>
            <div className="space-y-2">
              <div className="text-sm">
                <div className="font-medium">Impact:</div>
                <div className="text-charcoal-600">{m3Metadata.modality.attributes['Impact/Value']}/10</div>
              </div>
              <div className="text-sm">
                <div className="font-medium">Learning:</div>
                <div className="text-charcoal-600">{m3Metadata.modality.attributes['Learning Value']}/10</div>
              </div>
            </div>
          </div>
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-charcoal-900 flex items-center gap-2">
              <Zap className="h-5 w-5" />
              Dynamics
            </h3>
            <div className="space-y-2">
              <div className="text-sm">
                <div className="font-medium">Rules:</div>
                <div className="text-charcoal-600">{m3Metadata.dynamics.rules.length} active</div>
              </div>
              <div className="text-sm">
                <div className="font-medium">Processes:</div>
                <div className="text-charcoal-600">{m3Metadata.dynamics.processes.length} defined</div>
              </div>
            </div>
          </div>
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-charcoal-900 flex items-center gap-2">
              <Target className="h-5 w-5" />
              Evaluation
            </h3>
            <div className="space-y-2">
              <div className="text-sm">
                <div className="font-medium">Scope:</div>
                <div className="text-charcoal-600">{m3Metadata.evaluation.intent['Actor Scope']}</div>
              </div>
              <div className="text-sm">
                <div className="font-medium">Horizon:</div>
                <div className="text-charcoal-600">{m3Metadata.evaluation.intent['Time Horizon']}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
